import type { IAccount } from "@/modules/account/common/types";
import type {
  ICardTransferTemplateForm,
  ICardTransferTemplateRequest,
} from "@/modules/template/common/types";
import {
  useCreateCardTransferTemplateMutation,
  useGetCardTransferTemplateQuery,
  useUpdateCardTransferTemplateMutation
} from "@/modules/template/services";
import { cardTransferCommissionFieldKeys } from "@/modules/transaction/common/constants";
import { TransferKeys } from "@/modules/transaction/common/enum";
import {
  SaveBeneficiaryModal
} from "@/modules/transaction/components/CardTransfer/SaveBeneficiaryModal";
import { useCardTransfer } from "@/modules/transaction/hooks";
import { useLazyGetCardTransferCommissionQuery } from "@/modules/transaction/services";
import { CURRENCY, INPUT_MAX_LIMIT } from "@/shared/common/constants";
import { AppPaths, BeneficiaryType, SearchParamKeys } from "@/shared/common/enums";
import type { IExtendedObject } from "@/shared/common/types";
import {
  OwnAccountSelectOption,
  Select,
  TemplateLayout,
  TransferBeneficiaries
} from "@/shared/components";
import { formatCardNumber, getAmountOrNull, isValidAmount, trimAll, } from "@/shared/helpers";
import { useHasFormChanges } from "@/shared/hooks";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import type { SingleValue, TOption } from "@ab.uitools/ui-library/components/ReactSelect/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import uuid4 from "uuid4";

interface Props {
  accountsList: IAccount[];
  isEdit?: boolean;
  isCopy?: boolean;
  setIsCardNumberInvalid: Dispatch<SetStateAction<boolean>>;
}

export const CardTransferTemplateForm = ({
  accountsList, isEdit, setIsCardNumberInvalid
}: Props) => {
  const { t } = useTranslation();
  const { operationId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const idempotencyKey = useRef(uuid4()).current;
  const templateName = searchParams.get(SearchParamKeys.TemplateName);

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const {
    values,
    errors,
    reset,
    holder,
    isOpen,
    refetch,
    setValue,
    setIsOpen,
    fieldChanges,
    setIsBenSaved,
    canGetCommission,
    creditCardNumber,
    handleClickTarget,
    accountsFilteredList,
    handleChangeCardInput,
    handleChangeBeneficiaryCard,
  } = useCardTransfer({
    accountsList,
    setIsCardNumberInvalid,
    commissionFieldKeys: cardTransferCommissionFieldKeys,
  });

  const [getCommission] = useLazyGetCardTransferCommissionQuery();
  const [
    createCardTransferTemplateApi,
    { isLoading: isCreateLoading },
  ] = useCreateCardTransferTemplateMutation();
  const [
    updateCardTransferTemplateApi,
    { isLoading: isUpdateLoading },
  ] = useUpdateCardTransferTemplateMutation();

  const {
    data: cardTransferTemplate,
    isLoading: isGetTemplateLoading,
  } = useGetCardTransferTemplateQuery({
    id: operationId as string,
  }, {
    refetchOnMountOrArgChange: true,
    skip: !isEdit && !operationId
  });

  const cardTransferTemplateAction = useCallback((values: ICardTransferTemplateForm) => {
    const requestData: ICardTransferTemplateRequest = {
      ...values,
      name: templateName ?? "",
      amount: values.amount || null,
      creditCardNumber: trimAll(values.creditCardNumber),
      debitAccount: accountsList?.find(({ number }) => number === values.debitAccount
      ),
      commissionAccount: values.commissionAccount ? accountsList?.find(({ number }) => {
        return number === values.commissionAccount;
      }) : undefined,
    };

    return isEdit ?
      updateCardTransferTemplateApi({
        data: requestData,
        id: operationId,
      }).unwrap() :
      createCardTransferTemplateApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [accountsList, cardTransferTemplate]);

  const setDefaultValues = () => {
    const data = {
      creditCardNumber: formatCardNumber({ card: `${cardTransferTemplate?.creditCardNumber}` }),
      debitAccount: cardTransferTemplate?.debitAccount?.number,
      amount: getAmountOrNull(cardTransferTemplate?.amount),
      purpose: cardTransferTemplate?.purpose,
      commissionAccount: cardTransferTemplate?.commissionAccount?.number,
      currency: cardTransferTemplate?.debitAccount?.currency,
    };
    reset?.(data);
    setDefaultData(data);
    handleChangeCardInput(cardTransferTemplate?.creditCardNumber);
  };

  const currentData = useMemo(() => ({
    creditCardNumber: formatCardNumber({ card: `${values?.creditCardNumber}` }),
    debitAccount: values.debitAccount,
    amount: getAmountOrNull(values.amount),
    purpose: values.purpose,
    currency: values.currency,
    commissionAccount: values.commissionAccount,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if (isEdit && !!cardTransferTemplate?.transactionType) {
      handleChangeBeneficiaryCard(cardTransferTemplate.creditCardNumber);
      setDefaultValues();
    }
  }, [isEdit, cardTransferTemplate]);

  useEffect(() => {
    if (!isEdit && !templateName) {
      navigate(AppPaths.Templates);
    }
  }, [isEdit, templateName]);

  useEffect(() => {
    if (!isEdit) {
      setValue(TransferKeys.Purpose, t("transfer.budgetTransfer.purpose"));
    }
  }, [isEdit]);

  return (
    <>
      <TemplateLayout
        title={t("template.cardTransfer.title")}
        page="Card transfer template"
        getCommission={getCommission}
        accountsList={accountsList}
        isGetTemplateLoading={isGetTemplateLoading}
        isEdit={isEdit}
        isLoading={isCreateLoading || isUpdateLoading}
        hasChanges={hasChanges}
        canGetCommission={canGetCommission}
        fieldChanges={fieldChanges}
        saveTemplate={cardTransferTemplateAction}
      >
        <CollapseGroup
          className="collapse-group"
          items={[
            {
              content: <>
                <TransferBeneficiaries
                  name="creditCardNumber"
                  entryType={BeneficiaryType.Card}
                  label={t("transfer.beneficiaryCard")}
                  onChange={(v) => handleChangeBeneficiaryCard(v)}
                  placeholder={t("transfer.typeOrChooseACard")}
                />
                {holder && (
                  <div className="mt-8">
                    <Text>{t("transfer.card.nameOnCard")}</Text>
                    <Text className="my-8">
                      {holder}
                    </Text>
                  </div>
                )}
              </>,
              title: t("general.beneficiaryDetails"),
              value: "beneficiary_details",
              isOpen: true,
            },
            {
              content: <>
                <FormField
                  name="debitAccount"
                  className="mb-32"
                  shouldValidate
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Select
                      {...props}
                      required
                      isSearchable
                      className="custom-select"
                      options={accountsFilteredList}
                      label={t("transfer.debitAccount")}
                      placeholder={t("transfer.chooseDebitAccount")}
                      handleChange={(opt) => handleClickTarget(opt as SingleValue<TOption>)}
                      components={{ Option: OwnAccountSelectOption }}
                    />
                  )}
                />
                <FormField
                  name="amount"
                  className="mb-32"
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Input
                      {...props}
                      type="numeric"
                      allowNegative={false}
                      allowLeadingZeros={false}
                      label={t("general.amount")}
                      placeholder={t("general.enterAmount")}
                      thousandSeparator=","
                      decimalScale={values.currency === CURRENCY.AMD.label ? 1 : 2}
                      rightIconProps={{
                        Component: CURRENCY[values.currency]?.symbolIcon || "",
                      }}
                      isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                    />
                  )}
                />
                <FormField
                  name="purpose"
                  className="mb-8"
                  errorMessageIcon="infoFilled"
                  hideErrorMessage
                  As={(props) => (
                    <Textarea
                      {...props}
                      error={`${errors?.purpose?.message || ""}`}
                      label={t("general.purpose")}
                      placeHolder={t("general.enterYourPurpose")}
                      maxCount={INPUT_MAX_LIMIT}
                    />
                  )}
                />
              </>,
              title: t("transfer.transferDetails"),
              value: "transfer_details",
              isOpen: true,
            },
          ]}/>
      </TemplateLayout>
      <SaveBeneficiaryModal
        isOpen={isOpen}
        refetch={refetch}
        setIsOpen={setIsOpen}
        setIsBenSaved={setIsBenSaved}
        creditCardNumber={creditCardNumber}
      />
    </>
  );
};
